import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Live Google reviews (edge function `google-reviews`).
 *
 * Only the place id + an on/off switch are stored (company_info). Everything
 * Google returns — rating, count, review text, names, photos — is fetched live
 * and only ever held in React Query's in-memory cache for the session.
 */

/** Google's Places API only ever returns this many reviews for a business. */
export const GOOGLE_MAX_REVIEWS = 5;

export interface GooglePlaceSearchResult {
  placeId: string;
  name: string;
  address: string;
  rating: number | null;
  reviewCount: number;
}

export interface GoogleLiveReview {
  id: string;
  authorName: string;
  authorUrl: string | null;
  authorPhotoUrl: string | null;
  rating: number;
  text: string;
  relativeTime: string | null;
  publishTime: string | null;
  reviewUrl: string | null;
}

export interface GoogleLivePlace {
  placeId: string;
  name: string;
  rating: number | null;
  reviewCount: number;
  googleMapsUri: string | null;
  reviewsUrl: string;
  writeReviewUrl: string;
}

export interface GoogleLiveReviews {
  place: GoogleLivePlace;
  reviews: GoogleLiveReview[];
}

export interface GoogleReviewsSettings {
  placeId: string | null;
  enabled: boolean;
}

const FUNCTION_NAME = 'google-reviews';

export const googleSettingsKey = (orgId: string | null | undefined) => ['google-reviews-settings', orgId] as const;
export const googlePreviewKey = (orgId: string | null | undefined) => ['google-reviews-preview', orgId] as const;
export const publicGoogleReviewsKey = (orgId: string | null | undefined) => ['google-reviews-public', orgId] as const;

async function invokeGoogleReviews<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body });
  if (error) {
    // FunctionsHttpError exposes the JSON error payload via context.json().
    const message = await (error as { context?: { json?: () => Promise<{ error?: string }> } })
      .context?.json?.()
      .then((payload) => payload?.error)
      .catch(() => undefined);
    throw new Error(message || error.message || 'Google reviews request failed');
  }
  if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
    throw new Error((data as { error: string }).error);
  }
  return data as T;
}

type CompanyGoogleColumns = { google_place_id?: string | null; google_reviews_enabled?: boolean | null };

/** Reads the org's stored settings. `*` so it degrades to "not connected" before the migration runs. */
async function readSettings(orgId: string): Promise<GoogleReviewsSettings> {
  const { data, error } = await supabase.from('company_info').select('*').eq('organization_id', orgId).maybeSingle();
  if (error) throw error;
  const row = (data ?? {}) as CompanyGoogleColumns;
  return { placeId: row.google_place_id || null, enabled: row.google_reviews_enabled === true };
}

// ---------------------------------------------------------------------------
// Public website
// ---------------------------------------------------------------------------

/**
 * Live Google reviews for the tenant website, or `null` when the org hasn't
 * connected Google / switched it off. Only the org id is sent — the function
 * looks up the place id itself. Kept fresh for the browsing session so moving
 * between pages doesn't re-bill a Place Details call.
 */
export function usePublicGoogleReviews(orgId: string | undefined) {
  return useQuery({
    queryKey: publicGoogleReviewsKey(orgId),
    enabled: Boolean(orgId),
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<GoogleLiveReviews | null> => {
      // Cheap gate first (company_info is public): skip the function entirely
      // for orgs that haven't connected Google or have it switched off.
      const settings = await readSettings(orgId!);
      if (!settings.placeId || !settings.enabled) return null;
      const result = await invokeGoogleReviews<{ enabled: boolean } & Partial<GoogleLiveReviews>>({
        action: 'public',
        organizationId: orgId,
      });
      if (!result?.enabled || !result.place) return null;
      return { place: result.place, reviews: result.reviews ?? [] };
    },
  });
}

// ---------------------------------------------------------------------------
// Admin (owner)
// ---------------------------------------------------------------------------

export function useGoogleReviewsSettings(orgId: string | null | undefined) {
  return useQuery({
    queryKey: googleSettingsKey(orgId),
    enabled: Boolean(orgId),
    queryFn: () => readSettings(orgId!),
  });
}

/** Live preview of exactly what the website will show (works while switched off). */
export function useGoogleReviewsPreview(orgId: string | null | undefined, placeId: string | null | undefined) {
  return useQuery({
    queryKey: [...googlePreviewKey(orgId), placeId] as const,
    enabled: Boolean(orgId && placeId),
    staleTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<GoogleLiveReviews | null> => {
      const result = await invokeGoogleReviews<{ connected?: boolean } & Partial<GoogleLiveReviews>>({
        action: 'preview',
        organizationId: orgId,
      });
      if (!result?.connected || !result.place) return null;
      return { place: result.place, reviews: result.reviews ?? [] };
    },
  });
}

export function useSearchGooglePlaces(orgId: string | null | undefined) {
  return useMutation({
    mutationFn: async ({ query, town }: { query: string; town?: string }) => {
      if (!orgId) throw new Error('No organisation selected');
      const result = await invokeGoogleReviews<{ places: GooglePlaceSearchResult[] }>({
        action: 'search',
        organizationId: orgId,
        query,
        town,
      });
      return result.places ?? [];
    },
  });
}

function useSettingsMutation<TVars>(orgId: string | null | undefined, toBody: (vars: TVars) => Record<string, unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TVars) => {
      if (!orgId) throw new Error('No organisation selected');
      const result = await invokeGoogleReviews<{ settings: GoogleReviewsSettings }>({ ...toBody(vars), organizationId: orgId });
      return result.settings;
    },
    onSuccess: (settings) => {
      if (settings) queryClient.setQueryData(googleSettingsKey(orgId), settings);
      queryClient.invalidateQueries({ queryKey: googleSettingsKey(orgId) });
      queryClient.invalidateQueries({ queryKey: publicGoogleReviewsKey(orgId) });
    },
  });
}

/** Save the chosen business (switches website display on by default). */
export function useConnectGooglePlace(orgId: string | null | undefined) {
  return useSettingsMutation(orgId, ({ placeId, enabled }: { placeId: string; enabled?: boolean }) => ({
    action: 'connect',
    placeId,
    ...(enabled === undefined ? {} : { enabled }),
  }));
}

export function useSetGoogleReviewsEnabled(orgId: string | null | undefined) {
  return useSettingsMutation(orgId, (enabled: boolean) => ({ action: 'settings', enabled }));
}

export function useDisconnectGoogle(orgId: string | null | undefined) {
  return useSettingsMutation(orgId, (_: void) => ({ action: 'disconnect' }));
}
